import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import {
  AmpelBadgeComponent,
  ampelFor,
  AmpelState,
  countOpenDefects,
  openDefectKeys,
} from '../../../components/ampel-badge/ampel-badge.component';
import { StackBarComponent } from '../../../components/stack-bar/stack-bar.component';
import { FreigabeBewertungComponent } from '../freigabe-bewertung/freigabe-bewertung.component';
import { DefectRefChipsComponent } from '../../../components/defect-ref-chips/defect-ref-chips.component';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { ReleaseBlocker, ReleaseResultsFilters, ReleaseResultsSummary } from '../../../models/release-results.model';
import { Release, ReleaseStatus } from '../../../models/release.model';
import { ReleaseAssessment } from '../../../models/release-assessment.model';
import { formatIsoDateDe } from '../../../shared/date.util';
import { passRateTone, releaseResultsFilters } from '../test-results.utils';
import { ReleaseListState } from '../test-results.types';
import { umgebungsSchluessel } from '../../../shared/umgebung-name';
import { environmentFilterLabel } from '../../../shared/branch-name';

interface SummaryState {
  loading: boolean;
  error: boolean;
  data: ReleaseResultsSummary | null;
}

interface SummaryRequest {
  projectId: string;
  releaseId: string | null;
  filters: ReleaseResultsFilters;
  aktualisierung: number;
  versuch: number;
}

/**
 * Freigabe-Karte je Release (page-release-freigabe.jsx:381-450): Kopfzeile mit Stand
 * und Ampel, vier Kennzahlen und die Testfälle, die die Freigabe blockieren. Alle Zahlen kommen
 * aus einer Antwort — Legende, „Offene Blocker" und Blocker-Tabelle können nicht auseinanderlaufen.
 */
@Component({
  selector: 'app-release-freigabe',
  standalone: true,
  imports: [TranslatePipe, AmpelBadgeComponent, StackBarComponent, FreigabeBewertungComponent, DefectRefChipsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './release-freigabe.component.html',
  styleUrl: './release-freigabe.component.scss',
})
export class ReleaseFreigabeComponent {
  private readonly resultsService = inject(ReleaseResultsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly releases = input<Release[]>([]);
  readonly releaseId = input<string | null>(null);
  readonly releasesState = input<ReleaseListState>('loaded');
  readonly environment = input<string | null>(null);
  /** Wechselt nach einem Import; die Daten werden dann neu geholt. */
  readonly aktualisierung = input(0);
  readonly releasesNeuLaden = output<void>();

  readonly protokollExportieren = output<void>();

  private readonly bewertung = viewChild(FreigabeBewertungComponent);
  private readonly versuch = signal(0);

  protected readonly state = signal<SummaryState>({ loading: false, error: false, data: null });

  private readonly statusOverride = signal<{ releaseId: string; status: ReleaseStatus } | null>(null);

  protected readonly release = computed(() => {
    const found = this.releases().find((r) => r.id === this.releaseId());
    const override = this.statusOverride();
    if (!found || override?.releaseId !== found.id) return found ?? null;
    return { ...found, status: override.status };
  });
  protected readonly summary = computed(() => this.state().data);
  protected readonly regression = computed(() => {
    const block = this.summary()?.regression;
    return block?.plaene.length ? block : null;
  });
  protected readonly ampel = computed<AmpelState | null>(() => {
    const s = this.summary();
    return s ? ampelFor(s, countOpenDefects(s.blocker)) : null;
  });
  protected readonly offeneFehlerKeys = computed(() => openDefectKeys(this.summary()?.blocker ?? []));
  protected readonly blockerCount = computed(() => this.summary()?.blocker.length ?? 0);
  protected readonly blockerAufteilung = computed(() => {
    const blocker = this.summary()?.blocker ?? [];
    return {
      kritisch: blocker.filter((b) => b.prio === 'CRITICAL').length,
      failed: blocker.filter((b) => b.status === 'FAILED').length,
      blocked: blocker.filter((b) => b.status === 'BLOCKED').length,
    };
  });
  /** Altcode und Name derselben Umgebung (`STAGING`, „Staging“) zählen zusammen. */
  protected readonly blockerJeUmgebung = computed(() => {
    const jeUmgebung = new Map<string, { name: string; anzahl: number }>();
    for (const { umgebung } of this.summary()?.blocker ?? []) {
      if (!umgebung) continue;
      const schluessel = umgebungsSchluessel(umgebung);
      const bisher = jeUmgebung.get(schluessel);
      jeUmgebung.set(schluessel, { name: bisher?.name ?? umgebung, anzahl: (bisher?.anzahl ?? 0) + 1 });
    }
    return [...jeUmgebung.values()].sort((a, b) => a.name.localeCompare(b.name));
  });
  protected readonly umgebungLabel = environmentFilterLabel;
  protected readonly missingCoverage = computed(() => {
    const a = this.summary()?.abdeckung;
    return a ? a.gesamt - a.gedeckt : 0;
  });

  private readonly request = computed<SummaryRequest>(() => {
    return {
      projectId: this.projectId(),
      releaseId: this.releaseId(),
      filters: releaseResultsFilters(this.environment()),
      aktualisierung: this.aktualisierung(),
      versuch: this.versuch(),
    };
  });

  constructor() {
    toObservable(this.request)
      .pipe(
        switchMap((req) => {
          if (!req.releaseId) return of<SummaryState>({ loading: false, error: false, data: null });
          const previous = this.state().data;
          return this.resultsService.getSummary(req.projectId, req.releaseId, req.filters).pipe(
            map((data): SummaryState => ({ loading: false, error: false, data })),
            catchError(() => of<SummaryState>({ loading: false, error: true, data: null })),
            startWith<SummaryState>({ loading: true, error: false, data: previous }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.state.set(state));
  }

  protected erneutLaden(): void {
    this.versuch.update((v) => v + 1);
  }

  hasUnsavedChanges(): boolean {
    return this.bewertung()?.hasUnsavedChanges() ?? false;
  }

  /** `2026-10-15` → `15.10.2026`, ohne Zeitzonen-Umweg über `Date`. */
  protected formatStichtag(iso: string): string {
    return formatIsoDateDe(iso);
  }

  protected onDecisionSaved(assessment: ReleaseAssessment): void {
    this.statusOverride.set({ releaseId: assessment.releaseId, status: assessment.releaseStatus });
  }

  protected planKeys(blocker: ReleaseBlocker): string {
    return (blocker.plaene ?? [])
      .map((p) => p.key)
      .filter((key): key is string => !!key)
      .join(' · ');
  }

  protected readonly passRateTone = passRateTone;
}
